import {
    DefaultChatTransport,
    lastAssistantMessageIsCompleteWithToolCalls,
} from 'ai'
import type { FileUIPart } from 'ai'
import { computed, ref, watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePreferredDark } from '@vueuse/core'
import { generateId } from '@ai-sdk/ui-utils'
import { Chat } from '@ai-sdk/vue'
import { defineStore } from 'pinia'
import { useSafeLocalStorage } from '../useSafeLocalStorage'
import type {
    Chat as ChatRecord,
    AgentFileUpload,
    AgentInterface,
    AgentModel,
    AgentGatewayOptions,
    ChatMessageActions,
    MessageFeedback,
    RevisedAnswer,
    UIChatMessage,
    AgentTools,
} from 'models'
import {
    collectDeviceContext,
    collectGeolocation,
} from 'utils/src/device-context'
import { logger } from 'utils/src/logger'
import { getTextPart } from 'utils'
import { createChatbotApiClient, type ChatbotFile } from './api'
import type { PendingAttachment } from './types'

// The store exposes `chat` (a `Chat<UIChatMessage>` computed) whose inferred
// type transitively references JSONObject. Re-export it so consumers can emit
// portable declarations that name the type (TS2742) without an unused import.
export type { JSONObject } from '@ai-sdk/provider'

type ChatbotView = 'chat' | 'conversations' | 'profile' | 'auth' | 'files'

export function useChatbotStore(agentId: string) {
    return defineStore(`chatbot-${agentId}`, () => {
        const { t, te, locale } = useI18n({ useScope: 'global' })

        // === Configuration state ===
        const baseUrl = ref('')
        const basePath = ref<string | undefined>()
        const agentIdRef = ref(agentId)
        const name = ref<string | undefined>()
        const agentType = ref<'chat' | 'reasoning' | undefined>()
        const userId = ref<string | undefined>()
        const agentInterface = ref<AgentInterface | undefined>()
        const agentModel = ref<AgentModel | undefined>()
        const agentTools = ref<AgentTools | undefined>()
        const headers = ref<Record<string, string> | undefined>()
        const revisedAnswers = ref<RevisedAnswer[] | undefined>()
        /** Compatibility with PkChatbot `model` prop — overrides agentModel.model when set */
        const model = ref<string | undefined>()
        /** Raw actions from parent prop, filtered by agentType into `actions` */
        const actionsInput = ref<ChatMessageActions[] | undefined>()
        /** File upload configuration from agent settings */
        const agentFileUpload = ref<AgentFileUpload | undefined>()
        /** Fallback models for AI Gateway routing */
        const fallbackModels = ref<string[] | undefined>()
        /** Gateway options (ZDR, provider timeouts) */
        const gatewayOptions = ref<Partial<AgentGatewayOptions> | undefined>()
        /** External context set by the host application (e.g. via setContext() on the custom element). Validated server-side against the agent's externalContextSchema. */
        const externalContext = ref<Record<string, unknown> | undefined>()
        /** System-level theme override passed by the host application */
        const systemTheme = ref<'light' | 'dark' | undefined>()
        /** Hide the synthetic welcome message built from `initialMessage` (the fullscreen layout shows a greeting in its place) */
        const hideWelcomeMessage = ref(false)

        const prefersDark = usePreferredDark()
        const isDark = computed(() => {
            let theme = agentInterface.value?.theme
            if (!theme || theme === 'auto') {
                theme = systemTheme.value
            }
            if (!theme) {
                return prefersDark.value
            }
            return theme === 'dark'
        })

        const isReasoningAgent = computed(() => agentType.value === 'reasoning')

        const actions = computed<ChatMessageActions[] | undefined>(() => {
            if (!isReasoningAgent.value) {
                return actionsInput.value
            }
            return actionsInput.value?.filter(
                (action) =>
                    !['upvote', 'downvote', 'feedback'].includes(action),
            )
        })

        // === LocalStorage chatId ===
        const storageId = useSafeLocalStorage<string | null>(
            computed(() => `${agentIdRef.value}-chatId`),
            null,
        )

        watchEffect(() => {
            if (!storageId.value) {
                storageId.value = generateId()
            }
        })

        const localChatId = computed({
            get: () => storageId.value!,
            set: (value: string) => {
                storageId.value = value
            },
        })

        // === Chat error ===
        const chatError = ref<Error | undefined>()
        const isConversationBlocked = ref(false)

        const isConversationBlockedError = computed(
            () =>
                chatError.value?.message
                    ?.toLowerCase()
                    .includes('conversation-blocked') ?? false,
        )

        function isEntityNotFoundError(error: Error): boolean {
            try {
                const parsed = JSON.parse(error.message)
                return (
                    parsed.status === 404 ||
                    parsed.type?.includes('entity-not-found')
                )
            } catch {
                return false
            }
        }

        /**
         * When an anonymous session expires and rotates, the server returns a
         * 404 (entity-not-found) because the existing chatId belongs to the
         * previous userId. In that case we transparently generate a new chatId
         * and re-send the last user message so the conversation restarts
         * without the user noticing.
         */
        function handleChatError(error: Error) {
            if (isEntityNotFoundError(error)) {
                const pendingMessages = chat.value.messages.filter(
                    (m) => m.role === 'user',
                )
                const lastUserMessage =
                    pendingMessages[pendingMessages.length - 1]
                const lastUserText = lastUserMessage
                    ? getTextPart(lastUserMessage)
                    : undefined

                localChatId.value = generateId()
                chat.value.clearError()
                chatError.value = undefined

                if (lastUserText) {
                    chat.value.sendMessage({ text: lastUserText })
                }
                return
            }

            logger.error(error)
            chatError.value = error
            if (isConversationBlockedError.value) {
                isConversationBlocked.value = true
            }
        }

        // === API client ===
        const apiClient = computed(() =>
            createChatbotApiClient({
                baseUrl: baseUrl.value,
                basePath: basePath.value,
                headers: () => headers.value,
            }),
        )

        // === Chat streaming instance ===
        const storedGeolocation = ref<{
            geolocationLatitude: number
            geolocationLongitude: number
            geolocationAccuracy?: number
            geolocationCity?: string
        } | null>(null)

        const chat = computed(
            () =>
                new Chat<UIChatMessage>({
                    id: localChatId.value,
                    transport: new DefaultChatTransport({
                        api: apiClient.value.getChatEndpoint(
                            agentIdRef.value,
                            isReasoningAgent.value ? 'reasoning' : 'chat',
                        ),
                        prepareSendMessagesRequest: ({
                            messages: msgs,
                            id,
                        }) => ({
                            body: {
                                id,
                                messages: [msgs[msgs.length - 1]],
                                model: model.value,
                                prompt: agentModel.value?.prompt,
                                maxOutputTokens:
                                    agentModel.value?.maxOutputTokens,
                                temperature: agentModel.value?.temperature,
                                presencePenalty:
                                    agentModel.value?.presencePenalty,
                                frequencyPenalty:
                                    agentModel.value?.frequencyPenalty,
                                searchMode: agentModel.value?.searchMode,
                                limit: agentModel.value?.limit,
                                reasoning: agentModel.value?.reasoning,
                                webSearch: agentTools.value?.webSearch,
                                smoothStream:
                                    agentInterface.value?.smoothStream,
                                deviceContext: {
                                    ...collectDeviceContext(),
                                    ...(storedGeolocation.value ?? {}),
                                },
                                externalContext: externalContext.value,
                                disabledTools: agentTools.value?.disabled,
                                fallbackModels: fallbackModels.value,
                                gatewayOptions: gatewayOptions.value,
                            },
                        }),
                        credentials: 'include',
                        headers: headers.value,
                    }),
                    onToolCall: ({ toolCall }) => {
                        logger.log('Tool call:', toolCall)
                    },
                    sendAutomaticallyWhen:
                        lastAssistantMessageIsCompleteWithToolCalls,
                    onError: handleChatError,
                    onFinish: () => {
                        // A new chat is persisted on the backend only after the
                        // first message: reload the list if it's not there yet.
                        const id = localChatId.value
                        if (
                            !isReasoningAgent.value &&
                            !conversations.value.some((c) => c.id === id)
                        ) {
                            loadConversations(agentIdRef.value)
                        }
                    },
                }),
        )

        // === Messages ===
        const initialMessageResolved = computed(() => {
            const val = agentInterface.value?.initialMessage
            if (!val?.[locale.value]) {
                return te('message.defaultAgentWelcome')
                    ? t('message.defaultAgentWelcome')
                    : ''
            }
            return val[locale.value]
        })

        const welcomeMessage = computed<UIChatMessage>(() => ({
            id: generateId(),
            role: 'assistant',
            parts: [{ type: 'text', text: initialMessageResolved.value }],
        }))

        const messages = computed(() => {
            // Resolve any remaining `attachment://` URLs that haven't been
            // resolved server-side yet (e.g. in-flight streaming messages).
            const resolveInFlight = (msgs: UIChatMessage[]) =>
                msgs.map((msg) => {
                    if (msg.role !== 'user') {
                        return msg
                    }
                    const hasFileParts = msg.parts.some(
                        (p) => p.type === 'file',
                    )
                    if (!hasFileParts) {
                        return msg
                    }
                    return {
                        ...msg,
                        parts: msg.parts.map((p) => {
                            if (p.type !== 'file') {
                                return p
                            }
                            const fp = p as FileUIPart
                            if (fp.url.startsWith('attachment://')) {
                                const storedFileName = fp.url.slice(
                                    'attachment://'.length,
                                )
                                return {
                                    ...fp,
                                    url: apiClient.value.getMediaUrl(
                                        agentIdRef.value,
                                        localChatId.value,
                                        storedFileName,
                                    ),
                                } as FileUIPart
                            }
                            // Blob URLs (from pending uploads) are already
                            // usable as-is — don't append auth tokens.
                            if (fp.url.startsWith('blob:')) {
                                return fp
                            }
                            return {
                                ...fp,
                                url: apiClient.value.appendAuthToken(fp.url),
                            } as FileUIPart
                        }),
                    }
                })

            if (isReasoningAgent.value) {
                return resolveInFlight(chat.value.messages)
            }
            if (
                !hideWelcomeMessage.value &&
                chat.value.messages?.[0]?.role !== 'assistant'
            ) {
                return [
                    welcomeMessage.value,
                    ...resolveInFlight(chat.value.messages),
                ]
            }
            return resolveInFlight(chat.value.messages)
        })

        // === Localized placeholder ===
        const inputMessagePlaceholder = computed(() => {
            const val = agentInterface.value?.messagePlaceholder
            if (!val?.[locale.value]) {
                return te('placeholder.askAnything')
                    ? t('placeholder.askAnything')
                    : ''
            }
            return val[locale.value]
        })

        // === Conversations ===
        const conversations = ref<ChatRecord[]>([])
        const isLoadingConversations = ref(false)
        const chatFiles = ref<ChatbotFile[]>([])
        const isLoadingChatFiles = ref(false)

        // === Navigation ===
        const activeView = ref<ChatbotView>('chat')

        // === Message feedback state ===
        const messageFeedbacks = ref<MessageFeedback[]>([])
        const feedbackDialogMessage = ref<UIChatMessage | undefined>()
        const isFeedbackSubmitting = ref(false)
        const isFeedbackSubmitted = ref(false)
        const feedbackSubmitError = ref<string | undefined>()

        // === Lead form state ===
        const isLeadSubmitted = ref(false)
        const isLoadingSubmitLead = ref(false)
        const submitLeadError = ref<string | undefined>()

        // === Input ===
        const input = ref('')
        const pendingAttachments = ref<PendingAttachment[]>([])
        const lastSubmittedInput = ref('')
        const messageCountBeforeSubmit = ref(0)

        // === Internal loaders ===
        async function loadConversations(id: string) {
            if (isReasoningAgent.value) {
                conversations.value = []
                return
            }
            isLoadingConversations.value = true
            try {
                conversations.value = await apiClient.value.getConversations(
                    id,
                    userId.value,
                )
            } catch {
                conversations.value = []
            } finally {
                isLoadingConversations.value = false
            }
        }

        // Files uploaded in the current chat (the user's "My files" entries
        // tied to this conversation). No chat → nothing to load.
        async function loadChatFiles() {
            const chatId = localChatId.value
            if (!chatId) {
                chatFiles.value = []
                return
            }
            isLoadingChatFiles.value = true
            try {
                chatFiles.value = await apiClient.value.getChatFiles(chatId)
            } catch {
                chatFiles.value = []
            } finally {
                isLoadingChatFiles.value = false
            }
        }

        function getChatFileUrl(documentId: string, inline = false) {
            return apiClient.value.getChatFileDownloadUrl(documentId, inline)
        }

        // Exclude a file from the user's KB (soft-delete server-side); drop it
        // from the local list on success.
        async function deleteChatFile(documentId: string) {
            await apiClient.value.deleteChatFile(documentId)
            chatFiles.value = chatFiles.value.filter(
                (file) => file.documentId !== documentId,
            )
        }

        async function loadChat(id: string, chatId: string) {
            if (isReasoningAgent.value) {
                chat.value.messages = []
                return
            }
            try {
                const data = await apiClient.value.getChat(id, chatId)
                if (data) {
                    chat.value.messages = data.messages as UIChatMessage[]
                    isConversationBlocked.value = data.blocked ?? false
                } else {
                    chat.value.messages = []
                }
            } catch {
                chat.value.messages = []
            }
        }

        async function loadMessageFeedbacks(chatId: string) {
            try {
                messageFeedbacks.value =
                    await apiClient.value.getMessageFeedbacks(chatId)
            } catch {
                messageFeedbacks.value = []
            }
        }

        function upsertLocalFeedback(feedback: MessageFeedback) {
            const idx = messageFeedbacks.value.findIndex(
                (f) => f.messageId === feedback.messageId,
            )
            if (idx >= 0) {
                messageFeedbacks.value[idx] = feedback
            } else {
                messageFeedbacks.value.push(feedback)
            }
        }

        function extractFeedbackContext(message: UIChatMessage) {
            const msgList = messages.value
            const idx = msgList.findIndex((m) => m.id === message.id)
            const assistantMessage = getTextPart(message) ?? ''
            const prevUser = msgList
                .slice(0, idx)
                .reverse()
                .find((m) => m.role === 'user')
            const userQuestion = getTextPart(prevUser ?? message) ?? ''
            return { userQuestion, assistantMessage }
        }

        // === Actions ===
        async function initialize(id: string, chatId: string) {
            await Promise.all([
                loadChat(id, chatId),
                loadConversations(id),
                loadMessageFeedbacks(chatId),
            ])

            if (!storedGeolocation.value) {
                collectGeolocation((lat, lon) =>
                    apiClient.value.reverseGeocode(lat, lon, locale.value),
                ).then((geo) => {
                    if (geo) {
                        storedGeolocation.value = geo
                    }
                })
            }
        }

        async function handleSubmit() {
            // Snapshot text and attachments before any async operation to avoid
            // them being cleared by startNewChat() (reasoning agents) or by a
            // concurrent store update from the component.
            const text = input.value.trim()
            const fileParts: FileUIPart[] = pendingAttachments.value
                .filter(
                    (
                        att,
                    ): att is PendingAttachment & {
                        state: 'done'
                        mediaSource: NonNullable<
                            PendingAttachment['mediaSource']
                        >
                    } => att.state === 'done' && att.mediaSource !== undefined,
                )
                .map((att) => {
                    return {
                        type: 'file' as const,
                        mediaType: att.mediaSource.mimeType,
                        url: att.localUrl,
                        filename: att.mediaSource.name,
                        providerMetadata: {
                            '8bot': {
                                media: att.mediaSource,
                            },
                        },
                    }
                })

            if (isReasoningAgent.value && messages.value.length > 0) {
                await startNewChat()
            }

            // Snapshot state before sending so we can rollback on stop
            lastSubmittedInput.value = text
            messageCountBeforeSubmit.value = chat.value.messages.length

            // Chat<UIChatMessage> can't resolve the correct sendMessage overload
            // when using a custom message generic — cast is intentional.
            const sendMessage = chat.value.sendMessage as unknown as (msg: {
                text: string
                files?: FileUIPart[]
            }) => void
            sendMessage({
                text,
                files: fileParts.length > 0 ? fileParts : undefined,
            })
            input.value = ''
            clearPendingAttachments()
        }

        /**
         * Programmatically send a user message without the input snapshot/rollback
         * logic. Logs a warning and does nothing if the chat is not ready.
         */
        function sendMessage(text: string) {
            const trimmed = text.trim()
            if (!trimmed) {
                return
            }
            if (chat.value.status !== 'ready') {
                logger.warn(
                    '[useChatbotStore] sendMessage called before chat is ready. Listen for the "ready" event on the widget element.',
                )
                return
            }
            const send = chat.value.sendMessage as unknown as (msg: {
                text: string
            }) => void
            send({ text: trimmed })
        }

        async function stopGeneration() {
            const shouldRollback = lastSubmittedInput.value.length > 0
            await chat.value.stop()
            if (shouldRollback) {
                input.value = lastSubmittedInput.value
                chat.value.messages = chat.value.messages.slice(
                    0,
                    messageCountBeforeSubmit.value,
                )
                lastSubmittedInput.value = ''
            }
        }

        // Clear rollback state when generation completes naturally
        watch(
            () => chat.value.status,
            (status) => {
                if (status === 'ready' && lastSubmittedInput.value.length > 0) {
                    lastSubmittedInput.value = ''
                }
            },
        )

        function clearPendingAttachments(revokeAll = false) {
            for (const att of pendingAttachments.value) {
                // 'done' attachment localUrls may still be referenced in
                // FileUIPart.url inside message parts — skip revocation unless
                // explicitly requested (e.g. when starting a new chat).
                if (revokeAll || att.state !== 'done') {
                    URL.revokeObjectURL(att.localUrl)
                }
            }
            pendingAttachments.value = []
        }

        async function startNewChat() {
            lastSubmittedInput.value = ''
            await stopGeneration()
            localChatId.value = generateId()
            chat.value.clearError()
            chatError.value = undefined
            isConversationBlocked.value = false
            clearPendingAttachments(true)
            navigate('chat')
            return localChatId.value
        }

        async function regenerate(
            options?: Parameters<typeof chat.value.regenerate>[0],
        ) {
            if (chat.value.status !== 'ready') {
                lastSubmittedInput.value = ''
                await stopGeneration()
            }
            await chat.value.regenerate(options)
        }

        function addToolOutput(params: {
            tool: string
            toolCallId: string
            output: unknown
        }) {
            if (params.tool === 'requestGeolocation') {
                const output = params.output as
                    | {
                          latitude: number
                          longitude: number
                          accuracy?: number
                          city?: string
                      }
                    | { error: string }
                if ('latitude' in output) {
                    storedGeolocation.value = {
                        geolocationLatitude: output.latitude,
                        geolocationLongitude: output.longitude,
                        geolocationAccuracy: output.accuracy,
                        geolocationCity: output.city,
                    }
                }
            }
            chat.value.addToolOutput(params)
        }

        async function handleFileSelect(file: File) {
            const id = generateId()
            const localUrl = URL.createObjectURL(file)
            const attachment: PendingAttachment = {
                id,
                file,
                localUrl,
                state: 'uploading',
            }
            pendingAttachments.value = [...pendingAttachments.value, attachment]

            try {
                const mediaSource = await apiClient.value.uploadMedia(
                    agentIdRef.value,
                    localChatId.value,
                    file,
                )
                pendingAttachments.value = pendingAttachments.value.map((a) =>
                    a.id === id ? { ...a, state: 'done', mediaSource } : a,
                )
            } catch (err) {
                const message =
                    err instanceof Error ? err.message : 'Upload failed'
                pendingAttachments.value = pendingAttachments.value.map((a) =>
                    a.id === id ? { ...a, state: 'error', error: message } : a,
                )
            }
        }

        async function submitVote(
            message: UIChatMessage,
            vote: 'upvote' | 'downvote',
        ) {
            const { userQuestion, assistantMessage } =
                extractFeedbackContext(message)
            const feedback = await apiClient.value.submitMessageFeedback({
                messageId: message.id,
                chatId: localChatId.value,
                agentId: agentIdRef.value,
                vote,
                userQuestion,
                assistantMessage,
            })
            upsertLocalFeedback(feedback)
        }

        async function onUpvote(message: UIChatMessage) {
            await submitVote(message, 'upvote')
        }

        async function onDownvote(message: UIChatMessage) {
            await submitVote(message, 'downvote')
        }

        function onFeedback(message: UIChatMessage) {
            isFeedbackSubmitted.value = false
            feedbackSubmitError.value = undefined
            feedbackDialogMessage.value = message
        }

        async function onFeedbackSubmit(comment: string) {
            if (!feedbackDialogMessage.value) {
                return
            }
            try {
                isFeedbackSubmitting.value = true
                isFeedbackSubmitted.value = false
                feedbackSubmitError.value = undefined
                const { userQuestion, assistantMessage } =
                    extractFeedbackContext(feedbackDialogMessage.value)
                const feedback = await apiClient.value.submitMessageFeedback({
                    messageId: feedbackDialogMessage.value.id,
                    chatId: localChatId.value,
                    agentId: agentIdRef.value,
                    userQuestion,
                    assistantMessage,
                    comment,
                })
                upsertLocalFeedback(feedback)
                isFeedbackSubmitted.value = true
                await loadMessageFeedbacks(localChatId.value)
            } catch {
                feedbackSubmitError.value = te('message.feedbackSubmitError')
                    ? t('message.feedbackSubmitError')
                    : 'Failed to submit feedback'
            } finally {
                isFeedbackSubmitting.value = false
            }
        }

        async function onLeadSubmit(data: Record<string, unknown>) {
            try {
                isLoadingSubmitLead.value = true
                isLeadSubmitted.value = false
                submitLeadError.value = undefined
                await apiClient.value.submitLead({
                    agentId: agentIdRef.value,
                    chatId: localChatId.value,
                    ...data,
                })
                isLeadSubmitted.value = true
            } catch {
                submitLeadError.value = te('message.leadSubmitError')
                    ? t('message.leadSubmitError')
                    : 'Failed to submit lead'
            } finally {
                isLoadingSubmitLead.value = false
            }
        }

        async function renameChatTitle(chatId: string, title: string) {
            const updated = await apiClient.value.updateChatTitle(
                agentIdRef.value,
                chatId,
                title,
            )
            conversations.value = conversations.value.map((c) =>
                c.id === chatId ? { ...c, title: updated.title } : c,
            )
        }

        async function deleteChat(chatId: string) {
            await apiClient.value.deleteChat(agentIdRef.value, chatId)
            conversations.value = conversations.value.filter(
                (c) => c.id !== chatId,
            )
            if (localChatId.value === chatId) {
                await startNewChat()
            }
        }

        function navigate(view: ChatbotView) {
            activeView.value = view
        }

        return {
            // Configuration
            apiClient,
            baseUrl,
            basePath,
            agentId: agentIdRef,
            name,
            agentType,
            userId,
            agentInterface,
            agentModel,
            agentTools,
            headers,
            revisedAnswers,
            model,
            actionsInput,
            actions,
            agentFileUpload,
            fallbackModels,
            gatewayOptions,
            externalContext,
            systemTheme,
            hideWelcomeMessage,
            isDark,
            // Chat state
            chat,
            messages,
            conversations,
            isLoadingConversations,
            chatFiles,
            isLoadingChatFiles,
            input,
            pendingAttachments,
            localChatId,
            isConversationBlocked,
            inputMessagePlaceholder,
            chatError,
            // Navigation
            activeView,
            // Feedback state
            messageFeedbacks,
            feedbackDialogMessage,
            isFeedbackSubmitting,
            isFeedbackSubmitted,
            feedbackSubmitError,
            // Lead state
            isLeadSubmitted,
            isLoadingSubmitLead,
            submitLeadError,
            // Actions
            initialize,
            loadConversations,
            loadChatFiles,
            getChatFileUrl,
            deleteChatFile,
            handleSubmit,
            sendMessage,
            stopGeneration,
            startNewChat,
            regenerate,
            addToolOutput,
            handleFileSelect,
            clearPendingAttachments,
            onUpvote,
            onDownvote,
            onFeedback,
            onFeedbackSubmit,
            onLeadSubmit,
            navigate,
            renameChatTitle,
            deleteChat,
        }
    })()
}
